import anime from 'animejs';
import isMobile from 'is-mobile';
import forEach from 'lodash/forEach';
import isObject from 'lodash/isObject';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Transition, TransitionGroup } from 'react-transition-group';
import styled, { css } from 'styled-components';

import {
  useCarousel,
  useCookie,
  useEmailStopper,
  useIntentMessageOnInit,
  useMessageRenderer,
  useMessageScrollIntoView,
  usePubSubMessaging,
  useQuestions,
  useWelcomeMessage
} from '../../hooks';
import { useStores } from '../../stores';
import {
  detectBrowser,
  MESSAGE_ORDER_TYPE_FIRST,
  MESSAGE_TYPE_RECEIVED,
  MESSAGE_TYPE_SENT,
  MESSAGES_CONTENT_ID,
  SCROLLBAR_TOP,
  scrollTopValue
} from '../../utilities';
import Message, { MessageBase } from '../Message';
import NetworkQuality from '../NetworkQuality';
import Preloader from '../Preloader';
import Scrollbar from '../Scrollbar';

const MessageList = () => {
  const scrollbarRef = React.useRef(null);
  const messageRef = React.useRef(null);
  const { chat, config, network, chatWindow, ui, bot } = useStores();
  const { t } = useTranslation();
  const {
    scrollBar,
    network: networkConfig,
    header,
    message: {
      botBackgroundColor,
      userBackgroundColor,
      borderRadius: messageBorderRadius,
      animation: messageAnimation
    }
  } = config;
  const renderMessage = useMessageRenderer();
  const ref = useMessageScrollIntoView();
  const animationTimeout = messageAnimation ? messageAnimation.timeout || 300 : 0;
  useWelcomeMessage();
  usePubSubMessaging();
  useQuestions(scrollbarRef.current);
  useCarousel();
  useEmailStopper();
  useIntentMessageOnInit();
  useCookie();

  // Scroll settings for IE
  React.useEffect(() => {
    let top = 0;
    if (messageRef.current && chat.messages.length > bot.firstMessagesNumber) {
      top = messageRef.current.getBoundingClientRect().top;
    }

    if (detectBrowser.isIE && scrollbarRef.current && scrollbarRef.current.scrollTop && top) {
      const additionalHeight = scrollTopValue(
        header,
        ui.isVisibleExtraHeader,
        chatWindow.dimensions.height,
        SCROLLBAR_TOP
      );
      scrollbarRef.current.scrollTop(top + scrollbarRef.current.getScrollTop() - additionalHeight);
    }
  }, [
    bot.firstMessagesNumber,
    chat.messages.length,
    chatWindow.dimensions.height,
    header,
    header.avatar.height,
    header.height,
    ui.isVisibleExtraHeader
  ]);

  // scroll to bottom when jsonSelect menu is opened
  React.useEffect(() => {
    if (ui.isJsonSelectOpened) {
      scrollbarRef.current.scrollToBottom();
      ui.isJsonSelectOpened = false;
    }
  }, [ui.isJsonSelectOpened]);

  // reaction after closing the chat
  React.useEffect(
    () =>
      reaction(
        () => chatWindow.isClosed,
        isClosed => {
          if (isClosed) {
            chat.clearMessages();
          }
        },
        { delay: 300 }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // check config.intentUserInput and find input fields
  React.useEffect(() => {
    if (config.intentUserInput && config.intentUserInput.enabled) {
      forEach(config.intentUserInput.userSelection, field => {
        bot.formFields[field.inputName] = '';
        return bot.formFields;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // clear intentUserInput fields
  React.useEffect(() => {
    if (chat.messages.length === 0) {
      Object.keys(bot.formFields).map(key => {
        bot.formFields[key] = '';
        return bot.formFields;
      });
    }
  }, [bot.formFields, chat.messages.length]);

  return (
    <Root>
      <StyledScrollbar
        renderView={({ style, ...props }) => {
          return (
            <ScrollbarView
              {...props}
              style={{
                ...style,
                overflowX: 'hidden',
                bottom: '20px',
                top: SCROLLBAR_TOP
              }}
            />
          );
        }}
        autoHide
        ref={scrollbarRef}
        {...scrollBar}
      >
        <Spacer />
        <Content id={MESSAGES_CONTENT_ID} userBackgroundColor={userBackgroundColor}>
          <Transition
            unmountOnExit
            onEnter={node =>
              anime({
                targets: node,
                opacity: {
                  value: [0, 1],
                  duration: 300,
                  easing: 'linear',
                  delay: animationTimeout
                },
                easing: 'spring(1, 150)'
              })
            }
            onExit={node =>
              anime({
                targets: node,
                opacity: {
                  value: 0,
                  duration: 300,
                  easing: 'linear'
                },
                easing: 'spring(1, 150)'
              })
            }
            timeout={animationTimeout}
          >
            <Text>{t('noMessages')}</Text>
          </Transition>

          <TransitionGroup component={null}>
            {chat.messages
              .slice()
              .map((message, index, messages) => (
                <Transition
                  key={index}
                  onEnter={node => {
                    if (!messageAnimation) {
                      return;
                    }

                    if (
                      isObject(messageAnimation.receivedEnter) &&
                      message.type === MESSAGE_TYPE_RECEIVED
                    ) {
                      anime({
                        targets: node,
                        ...messageAnimation.receivedEnter
                      });

                      return;
                    }

                    if (
                      isObject(messageAnimation.sentEnter) &&
                      message.type === MESSAGE_TYPE_SENT
                    ) {
                      anime({
                        targets: node,
                        ...messageAnimation.sentEnter
                      });
                    }
                  }}
                  onExit={node => {
                    if (!messageAnimation) {
                      return;
                    }

                    if (
                      isObject(
                        messageAnimation.receivedExit && message.type === MESSAGE_TYPE_RECEIVED
                      )
                    ) {
                      anime({
                        targets: node,
                        ...messageAnimation.receivedExit
                      });
                      return;
                    }

                    if (isObject(messageAnimation.sentExit) && message.type === MESSAGE_TYPE_SENT) {
                      anime({
                        targets: node,
                        ...messageAnimation.sentExit
                      });
                    }
                  }}
                  timeout={animationTimeout}
                >
                  {renderMessage(message, index, messages, ref, messageRef)}
                </Transition>
              ))
              .slice()}
          </TransitionGroup>
          <Transition
            in={chat.isMessaging}
            unmountOnExit
            onEnter={node =>
              anime({
                targets: node,
                opacity: {
                  value: [0, 1],
                  duration: 300,
                  easing: 'linear'
                },
                easing: 'spring(1, 150)'
              })
            }
            onExit={node =>
              anime({
                targets: node,
                opacity: {
                  value: 0,
                  duration: 300,
                  easing: 'linear'
                },
                easing: 'spring(1, 150)'
              })
            }
            timeout={300}
          >
            <Message
              type={MESSAGE_TYPE_RECEIVED}
              orderType={MESSAGE_ORDER_TYPE_FIRST}
              backgroundColor={botBackgroundColor}
              borderRadius={messageBorderRadius}
            >
              <Preloader />
            </Message>
          </Transition>
        </Content>
      </StyledScrollbar>
      <NetworkQuality
        isOnline={network.isOnline}
        isSlowConnection={network.isSlowConnection}
        onRetry={() => network.ping(networkConfig.pooling.url, networkConfig.pooling.timeout)}
      />
    </Root>
  );
};

export default observer(MessageList);

const Root = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  margin: 0;
`;

const StyledScrollbar = styled(Scrollbar)`
  flex: 1;
`;

const Spacer = styled.div`
  display: inline-block;
  width: 3px;
  margin: 0;
`;

const ScrollbarView = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 10px;
`;

const Content = styled.div`
  /** padding-top = 10px - MessageBase margin-top */
  padding: 2px 20px;

  ${() =>
    isMobile() &&
    css`
      padding-right: 20px;
      padding-left: 20px;
    `}

  @media print {
    display: block;
  }

  ${MessageBase} {
    margin-top: 8px;
  }

  .projectSelector {
    background-color: transparent;
    border: 1px solid ${props => props.userBackgroundColor};
    display: inline-block;
    padding: 10px;
    margin: 10px 10px 10px 0;
    border-radius: 10px;
    cursor: pointer;
  }

  .projectSelector:hover {
    background-color: ${props => props.userBackgroundColor};
    color: #fff;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    & > div:last-of-type {
      padding-bottom: 20px;
    }
  }
`;

const Text = styled.div`
  font-size: 0.75em;
  color: grey;
`;
